/** @jsx jsx */
import React from 'react';

import { get } from 'lodash';

import { jsx } from '@emotion/core';
import GoogleOneTap from '../GoogleOneTap';
import Frame, { FrameContextConsumer } from './Frame';

import {
  GoogleOneTapCredential,
  GoogleOneTapError,
} from '@zapier/auth/src/types';

type GoogleOneTapFramedProps = {
  /**
   * A callback that is fired when Google One Tap encounters an error. Errors
   * can occur when we are unable to authenticate the user, or when the user
   * cancels the One Tap panel by clicking the X.
   */
  onGoogleAuthenticateError: (error: GoogleOneTapError) => void;

  /**
   * A callback that is fired when Google One Tap successfully authenticates
   * the selected user.
   */
  onGoogleAuthenticateSuccess: (credential: GoogleOneTapCredential) => void;

  /**
   * A callback that is fired when Google One Tap successfully loads.
   */
  onGoogleIframeLoaded?: () => void;

  /**
   * Enables the ability for users that have authenticated previously using
   * Google One Tap to automatically log in when this component renders.
   */
  shouldAutologin: boolean;

  /** Google Client ID key, typically provided as an environment variable */
  googleClientId?: string;
};

type State = {
  frameHeight?: string | number | null;
  isAutomaticallyLoggingin: boolean;
};

const Styles = {
  root: {
    border: 0,
    width: 'calc(100% + 20px)',
    height: 173,
    margin: '-22px 0 0 -1px', // Get rid of some of the padding within the iframe

    '@media screen and (max-width: 382px)': {
      width: 'calc(100% + 40px)',
      margin: '-22px 0 0 -11px',
    },
  },
};
/**
 * Renders Google One Tap within an `<iframe>`. This allows you to control where
 * on the page the Google One Tap panel should appear.
 */

class GoogleOneTapFramed extends React.Component<
  GoogleOneTapFramedProps,
  State
> {
  state: State = {
    frameHeight: null,
    isAutomaticallyLoggingin: false,
  };
  static defaultProps = {
    shouldAutologin: false,
  };
  onGoogleOneTapEvent = (event: MessageEvent) => {
    const { onGoogleIframeLoaded, onGoogleAuthenticateError } = this.props;
    const eventData = get(event, ['data'], {}) as {
      type: string;
      data: string;
    };

    switch (eventData.type) {
      case 'verifyPing':
        // This message is sent from the Google YOLO iframe, once we acknowledge it
        // the iframe will load.
        if (event.source && event.source.postMessage) {
          (event.source.postMessage as any)(
            {
              type: 'verifyAck',
              data: eventData.data,
            },
            '*'
          );

          if (onGoogleIframeLoaded) {
            onGoogleIframeLoaded();
          }
        } else {
          // Some clients are encountering an error that declares
          // `event.source.postMessage` is `undefined`. So in order to avoid
          // this crash, we will use the on error callback to inform the
          // parent that it needs to display an error message.
          //
          // See http://bit.ly/2EQ4y4y for further details on the error
          onGoogleAuthenticateError({
            type: 'noEventSource',
          });
        }

        break;
    }
  };

  componentDidMount() {
    window.addEventListener('message', this.onGoogleOneTapEvent);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onGoogleOneTapEvent);
  }

  onInitiateAutologin = () => {
    this.setState({
      isAutomaticallyLoggingin: true,
    });
  };
  onSetHeight = (height: string | number | null) => {
    const { isAutomaticallyLoggingin } = this.state;
    const { onGoogleAuthenticateError } = this.props;
    // Note: Google One Tap auto-login has a really weird bug, where if a user has > 1 account
    // it will just load the iframe without any callbacks to let us know that it was rendered.
    // 200 seems to be a reasonable number for 2 or more accounts in the iframe, but really this
    // is just an educated guess. If we fail, we will fall back and show the Google SSO button
    // with a CTA that says something like "Continue as 'peter.malmgren@zapier.com'" The
    // onGoogleAuthenticateError will hide the frame.

    if (parseInt(height as string) > 200 && isAutomaticallyLoggingin) {
      onGoogleAuthenticateError({
        type: 'multipleAccountsAutologin',
      });
      return;
    }

    this.setState({
      frameHeight: height,
    });
  };

  render() {
    const {
      googleClientId,
      onGoogleAuthenticateError,
      onGoogleAuthenticateSuccess,
      shouldAutologin,
    } = this.props;
    const { onSetHeight, onInitiateAutologin } = this;
    const heightStyle = {
      height: this.state.frameHeight,
    };
    return (
      <Frame css={Styles.root} style={heightStyle} title="Google One Tap">
        <FrameContextConsumer>
          {context => {
            return (
              <GoogleOneTap
                frameContext={context}
                googleClientId={googleClientId}
                onError={onGoogleAuthenticateError}
                onInitiateAutologin={onInitiateAutologin}
                onSetParentHeight={onSetHeight}
                onSuccess={onGoogleAuthenticateSuccess}
                shouldAutologin={shouldAutologin}
              />
            );
          }}
        </FrameContextConsumer>
      </Frame>
    );
  }
}

export default GoogleOneTapFramed;
